import { ApiError } from "@/core/api/types";
import { dayjs } from "@/core/utils/date";
import type { IcmPeriod } from "@/icm/types";
import { PAYROLL_RUN_STATUS } from "@/payroll/constants";
import type { EnrichedPayPeriod, IPayPeriod } from "@/payroll/payroll.d";
import { createCalculatedPayPeriod } from "@/payroll/utils/period-helpers";
import type {
  AiScriptRunDisplayStatus,
  AiScriptRunStatus,
  IAiScriptRun,
  IAiSpreadsheetSheet,
} from "./types";
import { WorkbookCellEditBatchError } from "./workbook-cell-edit-batcher";

export const DEFAULT_AI_SCRIPT_WORKBOOK_FILENAME = "payroll_result.xlsx";
const WORKBOOK_SESSION_ERROR =
  "Your session has expired. Sign in again and retry.";

const AI_SCRIPT_WORKBOOK_READ_ONLY_ERROR = "Read-only access";

export interface CreatedAiScriptRunForPayPeriod {
  startDate: string;
  endDate: string;
  run: Pick<IAiScriptRun, "id" | "status" | "created_at" | "sheet_scope">;
}

export type CreatedAiScriptRunByPeriodStart = Record<
  string,
  CreatedAiScriptRunForPayPeriod
>;

export const createdAiScriptRunsByPeriodKey = (businessId?: string | null) =>
  ["created-ai-script-runs-by-period", businessId] as const;

export const isAiScriptWorkbookAuthorizationError = (error: unknown) =>
  error instanceof ApiError && (error.status === 401 || error.status === 403);

// A 409 from an undo/redo replay: the workbook moved on since the edit was
// recorded (its base_version is stale), so the replay was rejected rather than
// clobbering the newer state.
export const isAiScriptWorkbookVersionConflictError = (error: unknown) =>
  error instanceof ApiError && error.status === 409;

export const isAiScriptWorkbookReadOnlyError = (error: unknown) =>
  isAiScriptWorkbookAuthorizationError(error) &&
  error instanceof ApiError &&
  error.message === AI_SCRIPT_WORKBOOK_READ_ONLY_ERROR;

export const getAiScriptWorkbookAuthorizationMessage = (error: unknown) => {
  if (error instanceof ApiError && error.status === 401) {
    return WORKBOOK_SESSION_ERROR;
  }

  if (isAiScriptWorkbookReadOnlyError(error)) {
    return "You have view-only access to this Grid, so the change was not saved.";
  }

  return isAiScriptWorkbookAuthorizationError(error)
    ? "You no longer have access to that Grid. Refreshing the Grid will show the sheets currently available to you."
    : error instanceof Error
      ? error.message
      : "Unknown error";
};

export const getAiScriptWorkbookDownloadErrorMessage = (error: unknown) => {
  if (error instanceof WorkbookCellEditBatchError) {
    return `The Grid changes could not be saved. Download stopped. ${error.message}`;
  }
  if (error instanceof ApiError && error.status === 401) {
    return "Your session has expired. Sign in again and retry the Grid download.";
  }

  if (isAiScriptWorkbookAuthorizationError(error)) {
    return "You do not have access to download this Grid. The visible sheets may have changed; refresh the page and try again.";
  }

  if (error instanceof ApiError && error.status === 404) {
    return "This Grid is no longer available to download.";
  }

  if (error instanceof ApiError && error.status >= 500) {
    return "This Grid could not be prepared. Please try again, or contact support if it keeps failing.";
  }

  return error instanceof Error
    ? error.message
    : "Please try again or contact support if the Grid still will not download.";
};

export const isAiSpreadsheetSheetEditable = (
  sheet: Pick<IAiSpreadsheetSheet, "editable"> | null | undefined,
) => sheet?.editable === true;

const AI_SCRIPT_STATUS_TO_PAYROLL_STATUS: Record<
  AiScriptRunStatus,
  AiScriptRunDisplayStatus
> = {
  pending: PAYROLL_RUN_STATUS.pending,
  running: PAYROLL_RUN_STATUS.processing,
  completed: PAYROLL_RUN_STATUS.completed,
  failed: PAYROLL_RUN_STATUS.failed,
};

export const hasActiveAiScriptRun = (
  period?: Pick<IPayPeriod, "latest_ai_script_run_status"> | null,
): boolean => {
  const status = period?.latest_ai_script_run_status;
  return status === "running" || status === "pending";
};

export const getAiScriptPayrollRunStatus = (
  status?: string | null,
): AiScriptRunDisplayStatus => {
  if (!status) return null;
  return (
    AI_SCRIPT_STATUS_TO_PAYROLL_STATUS[status as AiScriptRunStatus] ?? null
  );
};

export const applyAiScriptRunStatusToPeriods = (
  periods: IcmPeriod[],
  payPeriods: IPayPeriod[],
): IcmPeriod[] => {
  const payPeriodByStart = new Map(
    payPeriods.map((period) => [period.start_date, period]),
  );

  return periods.map((period) => {
    const payPeriod = payPeriodByStart.get(period.period_start);
    if (!payPeriod?.latest_ai_script_run_status) return period;

    return {
      ...period,
      has_payroll_run: true,
      latest_icm_run_status: getAiScriptPayrollRunStatus(
        payPeriod.latest_ai_script_run_status,
      ),
    };
  });
};

export const applyCreatedAiScriptRunToPayPeriods = (
  periods: EnrichedPayPeriod[] | undefined,
  { startDate, endDate, run }: CreatedAiScriptRunForPayPeriod,
) => {
  let didUpdate = false;
  let didFindPeriod = false;
  const nextPeriods = (periods ?? []).map((period) => {
    if (period.start_date !== startDate) return period;

    didFindPeriod = true;
    if (
      period.latest_ai_script_run_status &&
      period.latest_ai_script_run_at &&
      dayjs(period.latest_ai_script_run_at).valueOf() >=
        dayjs(run.created_at).valueOf()
    ) {
      return period;
    }

    didUpdate = true;
    return {
      ...period,
      latest_ai_script_run_status: run.status,
      latest_ai_script_run_at: run.created_at,
      active_ai_script_runs: [
        ...(period.active_ai_script_runs ?? []).filter(
          (active) => active.id !== run.id,
        ),
        {
          id: run.id,
          status: run.status,
          created_at: run.created_at,
          sheet_scope: run.sheet_scope ?? null,
        },
      ],
    };
  });

  if (didUpdate) return nextPeriods;
  if (didFindPeriod) return periods ?? [];

  const createdPeriod = {
    ...createCalculatedPayPeriod(dayjs(startDate), dayjs(endDate)),
    latest_ai_script_run_status: run.status,
    latest_ai_script_run_at: run.created_at,
    active_ai_script_runs: [
      {
        id: run.id,
        status: run.status,
        created_at: run.created_at,
        sheet_scope: run.sheet_scope ?? null,
      },
    ],
  };

  return [...(periods ?? []), createdPeriod].sort((a, b) =>
    b.start.diff(a.start),
  );
};

export const applyCreatedAiScriptRunsToPayPeriods = (
  periods: EnrichedPayPeriod[],
  createdRuns: CreatedAiScriptRunByPeriodStart | undefined,
) =>
  Object.values(createdRuns ?? {}).reduce(
    (nextPeriods, createdRun) =>
      applyCreatedAiScriptRunToPayPeriods(nextPeriods, createdRun),
    periods,
  );
